<template>
    <div
        class="upload-container h-full relative border-2 border-dashed rounded-lg text-center my-2 p-4 bg-cover bg-center bg-no-repeat flex items-center justify-center transition-all duration-300"
        :style="{
            backgroundImage: (previewImage || selectedImage) && !imageCleared ? `url(${previewImage || selectedImage})` : 'none',
            minHeight: `${height}px`,
        }"
        :class="[
            isDragOver ? 'drag-over' : '',
            imageChanged ? 'border-orange border-solid shadow-lg shadow-orange/20' : 'border-gray-300 dark:border-slate-700'
        ]"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @dragend.prevent="onDragLeave"
        @drop.prevent="onDrop"
    >
        <!-- Loading overlay -->
        <div
            v-if="regenerateImage"
            class="absolute inset-0 flex items-center justify-center bg-gray-100/75 dark:bg-slate-800/75 rounded-lg z-30"
        >
            <div class="flex flex-col items-center">
                <svg
                    class="animate-spin h-8 w-8 text-gray-500 dark:text-gray-400 mb-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>
                <span class="text-sm text-gray-600 dark:text-gray-300">
                    Generating new image...
                </span>
            </div>
        </div>
        <div
            v-if="!regenerateImage"
            class="drag-info d-none absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-gray-400"
        >
            <icon icon="no-image" :height="16" :width="16" />
            <br />
            <strong>Drop Cover Here</strong>
        </div>
        <div
            v-if="!selectedImage && !previewImage && (!props.image || imageCleared)"
            class="bg-slate-100/50 dark:bg-slate-700/50 backdrop-blur text-slate-800 dark:text-slate-100 rounded p-6 w-full max-w-3xl"
        >
            <div v-if="!regenerateImage" class="upload-text">
                <icon icon="no-image" :height="16" :width="16" />
                <strong>Upload or drag & drop an image here</strong>
                <p>We Support PNGs, JPEGs, WEBPs under 5MB</p>
            </div>
            <div v-if="!regenerateImage" class="upload-buttons">
                <button
                    class="btn btn-secondary flex items-center p-3 lg:px-2.5 lg:py-2 rounded whitespace-nowrap bg-white dark:bg-slate-800"
                    @click="triggerFileUpload"
                >
                    <icon icon="upload" :height="16" :width="16" />
                    <span class="ml-2 text-sm font-medium">Upload</span>
                </button>

                <button
                    class="btn btn-secondary flex items-center p-3 lg:px-2.5 lg:py-2 rounded whitespace-nowrap bg-white dark:bg-slate-800"
                    @click="fetchUnsplashImages"
                    :style="{
                        cursor: isDestinationSet ? 'pointer' : 'not-allowed',
                    }"
                >
                    <icon icon="rotate" :height="16" :width="16" />
                    <span class="ml-2 text-sm font-medium">Generate</span>
                </button>
            </div>

            <input
                type="file"
                @change="handleFileUpload"
                accept="image/jpeg, image/png, image/webp"
                class="hidden-input"
                ref="fileInput"
            />
        </div>

        <!-- Save/cancel overlay shown when there are unsaved changes -->
        <Transition name="fade">
            <div
                v-if="imageChanged"
                class="absolute bottom-0 right-0 px-3 py-3 flex items-center gap-2 z-50"
            >
                <div class="flex items-center gap-2">
                    <button
                        type="button"
                        class="w-9 h-9 flex items-center justify-center bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100 rounded-full shadow-md transition-all duration-200"
                        @click="cancelImageChange"
                        title="Discard changes"
                    >
                        <icon icon="x" :height="18" :width="18" />
                    </button>
                    <button
                        type="button"
                        class="px-4 py-2 text-sm font-medium bg-white dark:bg-slate-800 hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-slate-700 shadow-sm rounded-md transition-all duration-200 flex items-center gap-2"
                        @click="handleSaveImage()"
                        :disabled="isSaving"
                    >
                        <svg v-if="isSaving" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <icon v-else icon="check" :height="20" :width="20" />
                        {{ isSaving ? 'Saving...' : 'Save Image' }}
                    </button>
                </div>
            </div>
        </Transition>

        <!-- Image actions shown when image is saved -->
        <div v-if="!imageChanged && !imageCleared && (selectedImage || previewImage)" class="image-actions z-50">
            <a
                v-if="selectedImage"
                class="icon-button"
                href="#"
                @click="triggerFileUpload"
                @mouseover="showTooltip('upload')"
                @mouseout="hideTooltip('upload')"
            >
                <icon icon="upload" :height="16" :width="16" />
                <div
                    v-if="tooltipVisible.upload"
                    class="absolute top-[-35px] left-[25%] transform -translate-x-1/2 z-[1000] block opacity-100 bg-[#213e44] text-white p-[5px] rounded text-xs whitespace-nowrap"
                >
                    Upload
                </div>
            </a>

            <a
                class="icon-button"
                href="#"
                @click="fetchUnsplashImages"
                @mouseover="showTooltip('regenerate')"
                @mouseout="hideTooltip('regenerate')"
            >
                <icon icon="rotate" :height="16" :width="16" />
                <div
                    v-if="tooltipVisible.regenerate"
                    class="absolute top-[-35px] left-[50%] transform -translate-x-1/2 z-[1000] block opacity-100 bg-[#213e44] text-white p-[5px] rounded text-xs whitespace-nowrap"
                >
                    Regenerate
                </div>
            </a>
            <input
                type="file"
                @change="handleFileUpload"
                accept="image/jpeg, image/png, image/webp"
                class="hidden-input"
                ref="fileInput"
            />
            <a
                v-if="selectedImage"
                class="icon-button"
                href="#"
                @click="clearImage"
                @mouseover="showTooltip('delete')"
                @mouseout="hideTooltip('delete')"
            >
                <icon icon="trash" :height="16" :width="16" />
                <div
                    v-if="tooltipVisible.delete"
                    class="absolute top-[-35px] left-[75%] transform -translate-x-1/2 z-[1000] block opacity-100 bg-[#213e44] text-white p-[5px] rounded text-xs whitespace-nowrap"
                >
                    Delete
                </div>
            </a>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount } from 'vue';
import { notify } from 'notiwind';
import { useTripsStore } from '@/store/trips';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    trip: {
        type: Object,
        default: () => {
            id: null;
        },
    },
    query: {
        type: String,
        default: '',
    },
    image: {
        type: String,
        default: '',
    },
    height: {
        type: Number,
        default: 200,
    },
    showConfirmation: {
        type: Boolean,
        default: false,
    },
});
const paddingTop = computed(() => {
    if (props.height <= 200) return 25;
    return (props.height - 200) / 2;
});
const selectedImage = ref(props.image);
const previewImage = ref(null);
const emit = defineEmits(['image-selected', 'save']);
const imageLoaded = ref(false);
const imageChanged = ref(false);
const fileInput = ref(null);
const isDragOver = ref(false);
const imageSource = ref(null); // 'upload' or 'generate'
const imageCleared = ref(false);
const lastUnsplashFetchTime = ref(null);
const unsplashMinutesRateLimit = 5;
const unsplashRateLimit = 15;
const currentUnsplashFetchCount = ref(0);
const uploadImageMaxMbSize = 5;
const regenerateImage = ref(false);
const isSaving = ref(false);
const NOTIFICATION_DURATION_MS = 5000;

const isDestinationSet = computed(() => {
    return !!props.query;
});

const onDragOver = () => {
    isDragOver.value = true;
};

const onDragLeave = () => {
    isDragOver.value = false;
};
const onDrop = (event) => {
    isDragOver.value = false;
    const file = event.dataTransfer.files[0];
    handleFile(file);
};

const handleFileUpload = (event) => {
    const file = event.target.files[0];
    handleFile(file);
};

const handleFile = (file) => {
    const maxSize = uploadImageMaxMbSize * 1024 * 1024;
    if (
        file &&
        (file.type === 'image/jpeg' ||
            file.type === 'image/png' ||
            file.type === 'image/webp') &&
        file.size <= maxSize
    ) {
        const reader = new FileReader();
        reader.onload = (e) => {
            previewImage.value = e.target.result;
            imageLoaded.value = true;
            imageCleared.value = false;
            imageSource.value = 'upload';
            emit('image-selected', e.target.result);
            imageChanged.value = true;
            notify({
                group: 'info',
                title: 'Image preview ready',
                text: 'Click Save to apply this cover image before continuing',
            }, NOTIFICATION_DURATION_MS);
        };
        reader.readAsDataURL(file);
    } else {
        notify({
            group: 'error',
            title: 'Invalid Image',
            text: `Please upload a JPEG, PNG or WEBP image less than ${uploadImageMaxMbSize}MB`,
        });
    }
};

const handleSaveImage = () => {
    isSaving.value = true;
    selectedImage.value = previewImage.value;
    emit('save', selectedImage.value);
    
    setTimeout(() => {
        isSaving.value = false;
        imageChanged.value = false;
        notify({
            group: 'success',
            title: 'Cover updated',
            text: 'Your cover image has been saved successfully',
        });
    }, 500);
};

const fetchUnsplashImages = async () => {
    if (!isDestinationSet.value) {
        useTripsStore().tripFormErrors.country = [
            'Please select a destination to generate image',
        ];
        return;
    }
    if (
        lastUnsplashFetchTime.value &&
        new Date() - lastUnsplashFetchTime.value <
            unsplashMinutesRateLimit * 60 * 1000
    ) {
        if (currentUnsplashFetchCount.value >= unsplashRateLimit) {
            notify({
                group: 'error',
                title: 'Rate Limit Exceeded',
                text: 'Wait a few minutes and try again later',
            });
            return;
        } else {
            currentUnsplashFetchCount.value++;
        }
    } else {
        lastUnsplashFetchTime.value = new Date();
        currentUnsplashFetchCount.value = 1;
    }

    if (!isDestinationSet.value) return;
    imageLoaded.value = false;
    regenerateImage.value = true;
    try {
        const response = await ApiService.apiCall(
            `/media/random-image/${props.query}`,
            'get'
        );
        const newImage = response.data.images.regular;
        const img = new Image();

        img.onload = () => {
            regenerateImage.value = false;
            previewImage.value = newImage;
            imageCleared.value = false;
            imageSource.value = 'generate';
            emit('image-selected', previewImage.value);
            imageChanged.value = true;
            imageLoaded.value = true;
            if (!props.trip.id) {
                handleSaveImage();
            } else {
                notify({
                    group: 'info',
                    title: 'Image preview ready',
                    text: 'Click Save to apply this cover image before continuing',
                }, NOTIFICATION_DURATION_MS);
            }
        };
        img.src = newImage;
    } catch (error) {
        console.error(error);
        notify({
            group: 'warning',
            title: 'Image Not Available',
            text: "We couldn't automatically generate an image for this destination. Please upload your own image.",
        });
        imageLoaded.value = true;
        regenerateImage.value = false;
    }
};

const triggerFileUpload = () => {
    fileInput.value.value = '';
    fileInput.value.click();
};

const resetImageState = () => {
    selectedImage.value = null;
    previewImage.value = null;
    imageLoaded.value = false;
    imageChanged.value = false;
    imageSource.value = null;
    imageCleared.value = false;
};

const clearImage = () => {
    resetImageState();
    imageCleared.value = true;
    emit('image-selected', null);
};

const cancelImageChange = () => {
    previewImage.value = null;
    imageChanged.value = false;
    imageSource.value = null;
    emit('image-selected', selectedImage.value);
};

watch(
    () => props.image,
    (newValue) => {
        if (newValue) {
            if (newValue && !imageChanged.value) {
                const img = new Image();
                img.onload = () => {
                    selectedImage.value = newValue;
                    imageLoaded.value = true;
                };
                img.src = newValue;
            }
        } else {
            // Clear internal state when image prop is null (new trip)
            resetImageState();
        }
    },
    { immediate: true }
);

watch(
    () => props.trip?.id,
    (newTripId, oldTripId) => {
        // If trip ID changes from existing to null/undefined (new trip), clear state
        if (oldTripId && !newTripId) {
            resetImageState();
        }
    }
);

const tooltipVisible = ref({
    upload: false,
    regenerate: false,
    delete: false,
});

const showTooltip = (tooltip) => {
    tooltipVisible.value = { ...tooltipVisible.value, [tooltip]: true };
};

const hideTooltip = (tooltip) => {
    tooltipVisible.value = { ...tooltipVisible.value, [tooltip]: false };
};

onBeforeUnmount(() => {
    if (imageChanged.value && imageSource.value === 'generate') {
        handleSaveImage();
    }
});
</script>

<style scoped>
.upload-container {
    & .drag-info {
        display: none;
    }

    &.drag-over {
        background-color: #f0f0f0;
        background-image: none !important;

        & .upload-buttons,
        & .upload-text {
            display: none;
        }

        & .drag-info {
            display: block;
        }
    }
}

.upload-text strong {
    display: block;
    font-size: 18px;
    margin-bottom: 5px;
}

.upload-text p {
    margin-bottom: 20px;
}

.upload-buttons {
    display: flex;
    justify-content: center;
    gap: 10px;
}

.hidden-input {
    display: none;
}

.image-actions {
    position: absolute;
    bottom: 10px;
    right: 10px;
    display: flex;
    gap: 10px;
}

.icon-button {
    background-color: #fff;
    border-radius: 8px;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
}

.icon-button i {
    color: #000;
    margin: auto;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}
</style>
